import { Routes, Route, Navigate } from 'react-router-dom';
import ServicesIndex from './ServicesIndex';
import CategoryLanding from './CategoryLanding';
import ServiceCatalogDetail from './ServiceCatalogDetail';
import { DomainDetail } from './DomainDetail';
import { DomainSetupWizard } from './domain/setup/DomainSetupWizard';
// Guided CDN/WAF (vEdge/raven) self-serve onboarding — disabled: the only inbound
// link (Overview "Onboarding Domain Now") now points at the CDN landing, and WAF
// self-activation is contact-sales only. Kept for re-enable if self-serve returns.
// import { OnboardingWizard } from './onboarding/OnboardingWizard';
import { WaapOnboardingLauncher } from './waap/WaapOnboardingLauncher';
import { FastChannelPage } from './fastchannel/FastChannelPage';
import { ComputePage } from './compute/ComputePage';
import { KubernetesPage } from './kubernetes/KubernetesPage';
import { DatabasePage } from './database/DatabasePage';
import { PrivateZonePage } from './private-zones/PrivateZonePage';
import { HealthcheckPage } from './healthcheck/HealthcheckPage';
import { AesPage } from './aes/AesPage';
import { VodPage } from './vod/VodPage';
import { LivePage } from './live/LivePage';
import { S3Page } from './oss/S3Page';
import { WafPage } from './waf/WafPage';
import { OrganizationsPage } from './organizations/OrganizationsPage';
import { CdnHomePage } from './cdn/CdnHomePage';
import { CdnConsole } from './cdn/CdnConsole';
import { CertInventoryPage } from './certinvent/CertInventoryPage';
import { WorkspaceGate } from './certinvent/WorkspaceGate';
import BusinessUsagePage from './usage/BusinessUsagePage';
import { WaapConsole } from './waap/WaapConsole';
import { WaapLandingGate } from './waap/WaapLandingGate';

/**
 * Services module — per-category landing pages + per-service consoles.
 *
 *   /services                         → category index grid (ServicesIndex)
 *   /services/<key>                   → category landing (CategoryLanding)
 *   /services/catalog/:cat/:product   → per-product catalog landing page
 *   /services/cdn                     → CDN home (orchestrator directory)
 *   /services/cdn-landing             → CDN category landing
 *   /services/cdn/:serviceId/*        → scoped CDN console
 *   /services/compute                 → Compute category landing
 *   /services/compute/*               → Techev Cloud (OpenNebula VMs)
 *   /services/kubernetes              → Kubernetes category landing
 *   /services/kubernetes/*            → Managed Kubernetes console
 *   /services/security                → Security category landing
 *   … (other consoles unchanged)
 */
export default function ServicesPage() {
  return (
    <Routes>
      {/* ── Index: category grid ──────────────────────────────────────────── */}
      <Route index element={<ServicesIndex />} />

      {/* ── Per-category landings ─────────────────────────────────────────── */}
      <Route path="security" element={<CategoryLanding categoryKey="security" />} />
      <Route path="compute" element={<CategoryLanding categoryKey="compute" />} />
      <Route path="kubernetes" element={<CategoryLanding categoryKey="kubernetes" />} />
      <Route path="databases" element={<CategoryLanding categoryKey="databases" />} />
      <Route path="storage" element={<CategoryLanding categoryKey="storage" />} />
      <Route path="observability" element={<CategoryLanding categoryKey="observability" />} />
      <Route path="management" element={<CategoryLanding categoryKey="management" />} />
      <Route path="streaming" element={<CategoryLanding categoryKey="streaming" />} />
      <Route path="cdn" element={<CategoryLanding categoryKey="cdn" />} />

      <Route path="catalog/:category/:product" element={<ServiceCatalogDetail />} />

      {/* ── CDN onboarding wizard (disabled — see import note above) ───────── */}
      {/* <Route path="onboarding" element={<OnboardingWizard />} /> */}

      {/* ── Domain sub-flows ─────────────────────────────────────────────── */}
      <Route path="domains/new" element={<DomainSetupWizard />} />
      <Route path="domains/:domainId/*" element={<DomainDetail />} />

      {/* ── Retired org-wide CDN landings → CDN home directory ───────────── */}
      <Route path="overview" element={<Navigate to="/services/cdn/home" replace />} />
      <Route path="domains" element={<Navigate to="/services/cdn/home" replace />} />
      <Route path="analytics" element={<Navigate to="/services/cdn/home" replace />} />
      <Route path="usage" element={<Navigate to="/services/cdn/home" replace />} />

      {/* ── Org-wide SSL inventory ────────────────────────────────────────── */}
      <Route
        path="certinvent"
        element={
          <WorkspaceGate>
            <CertInventoryPage />
          </WorkspaceGate>
        }
      />

      {/* ── CDN ──────────────────────────────────────────────────────────── */}
      {/* /services/cdn = generic category landing; the orchestrator directory
          (CdnHomePage) lives under /cdn/home, reached once a service exists. */}
      <Route path="cdn/home" element={<CdnHomePage />} />
      <Route path="cdn/usage" element={<BusinessUsagePage />} />
      <Route path="cdn/:serviceId/domains/new" element={<DomainSetupWizard />} />
      <Route path="cdn/:serviceId/domains/:domainId/*" element={<DomainDetail />} />
      <Route path="cdn/:serviceId/*" element={<CdnConsole />} />

      {/* ── WAAP ─────────────────────────────────────────────────────────── */}
      <Route path="waap" element={<WaapLandingGate />} />
      <Route path="waap/onboarding" element={<WaapOnboardingLauncher />} />
      <Route path="waap/:serviceId/*" element={<WaapConsole />} />

      {/* ── Service consoles ─────────────────────────────────────────────── */}
      <Route path="fastchannel/*" element={<FastChannelPage />} />
      <Route path="compute/*" element={<ComputePage />} />
      <Route path="kubernetes/*" element={<KubernetesPage />} />
      <Route path="database/*" element={<DatabasePage />} />
      <Route path="private-zones/*" element={<PrivateZonePage />} />
      <Route path="healthcheck/*" element={<HealthcheckPage />} />
      <Route path="aes/*" element={<AesPage />} />
      <Route path="vod/*" element={<VodPage />} />
      <Route path="live/*" element={<LivePage />} />
      <Route path="oss/*" element={<S3Page />} />
      <Route path="waf/*" element={<WafPage />} />
      <Route path="organizations/*" element={<OrganizationsPage />} />
    </Routes>
  );
}
